import { useInfiniteQuery } from '@tanstack/react-query';
import { get } from '../api/api';
import { API_URLS } from '../constants/apiUrl';
import { QueryParamObject, toQueryString } from '../constants/utilityFunctions';
interface IUseFetchMatters {
  withFavoriteInformation?: boolean;
  isFavorite?: boolean;
  withDefaultTransactionType?: boolean;
  withInvoicesOverdueInDays?: boolean;
  withHasSubMatters?: boolean;
  withHasLinkedTransactionTypes?: boolean;
  withShowOnlyOwnEntries?: boolean;
  myOwn?: boolean;
  relations?: string[];
  sortBy?: string[];
  extraFilters?: Record<string, string | number | string[]>;
  pageSize?: number;
  search?: string;
  fields?: string[];
}
export const useFetchMatters = (params: IUseFetchMatters) => {
  const fetchMatters = async ({ pageParam = 1 }) => {
    const { relations: matterRelations, extraFilters: filters, myOwn, ...rest } = params;
    const queryString = new URLSearchParams(
      Object.keys(rest)?.length ? toQueryString(rest as QueryParamObject) : '',
    );

    Object.entries(filters ?? {}).forEach(([key, value]) => {
      queryString.append(`filter.${key}`, String(value));
    });

    (matterRelations ?? []).forEach((rel) => queryString.append('relations', rel));

    queryString.append('page', String(pageParam));
    queryString.append('limit', String(params.pageSize));
    try {
      return await get(
        `${myOwn ? API_URLS.MY_MATTERS_LIST : API_URLS.MATTERS_LIST}?${queryString.toString()}`,
      );
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw new Error((error as { message: string }).message || 'Failed to fetch transactions'); // Force error throwing
    }
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: [
      params?.extraFilters?.relatedEmails ? 'suggested-matters-list' : 'matters-list',
      params,
    ],
    queryFn: fetchMatters,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage?.meta?.totalItems ?? 0;
      const fetchedCount = allPages.flatMap((page) => page.data).length;
      return fetchedCount < totalCount ? allPages.length + 1 : undefined;
    },
  });
  return {
    data: data?.pages.flatMap((page) => page.data) ?? [],
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  };
};
